/**
 * One place deciding what colour a domain state reads as.
 *
 * Ten helpers had grown up for this, two of them sharing a name in different
 * files, and they disagreed: the same completion percentage was banded at 75/40
 * in one place and 90/60 in another, so a programme could read amber on one
 * screen and green on the next.
 *
 * Tones only. Nothing here returns a class name, so a screen cannot pick a
 * colour without going through a component that also carries an icon.
 *
 * @module components/shared/status-tones
 */

import type { ReadinessState } from "@/server/types/readiness";
import type { WorkflowState } from "@/server/types/workflow";

/** The vocabulary every status component maps onto. */
export type Tone = "success" | "warning" | "error" | "info" | "neutral";

/**
 * Where a programme sits in the approval workflow.
 *
 * `error` is reserved for a state a person has to act on: changes were
 * requested. Waiting on somebody is `warning`, done is `success`, and a state
 * that is simply a resting place is `neutral`.
 *
 * @param state - The programme's workflow state.
 * @returns The tone it reads as.
 */
export function workflowStateTone(state: WorkflowState): Tone {
  switch (state) {
    case "Approved":
    case "Final Descriptor Signed Off":
    case "Exported and Locked":
      return "success";
    case "Changes Required":
      return "error";
    case "Awaiting Internal Review":
    case "Internal Review":
    case "Awaiting QA Approval":
      return "warning";
    case "In Development":
    case "Ready for Development":
      return "info";
    case "Draft":
    case "Archived":
      return "neutral";
    default:
      return "neutral";
  }
}

/**
 * How far up the readiness ladder a programme has climbed.
 *
 * @param state - The evaluated readiness state.
 * @returns The tone it reads as.
 */
export function readinessTone(state: ReadinessState): Tone {
  switch (state) {
    case "Exported and locked":
    case "Ready for final export":
      return "success";
    case "Ready for approval":
    case "Ready for internal review":
      return "info";
    case "Structurally complete":
      return "warning";
    case "Incomplete":
      return "error";
    default:
      return "neutral";
  }
}

/** Where a module sits in its own review cycle. */
export function moduleReviewTone(status: string): Tone {
  switch (status) {
    case "Approved":
      return "success";
    case "Submitted for Review":
      return "warning";
    case "Changes Requested":
      return "error";
    // Not a review state the module reached, but the reason it has not: the
    // overview counts it beside the four, and the same quantity is `warning`
    // on the portfolio panel of the same screen.
    case "Unassigned":
      return "warning";
    case "Draft":
      return "neutral";
    default:
      return "neutral";
  }
}

/** Where an approved revision sits in the portfolio lifecycle. */
export function lifecycleTone(status: string): Tone {
  switch (status) {
    case "current":
      return "success";
    case "approved-future":
      return "info";
    case "superseded":
      return "neutral";
    default:
      return "neutral";
  }
}

/**
 * A completion percentage, on one band for the whole application.
 *
 * The header banded this at 75 and 40 while the validation report used 90 and
 * 60, so the same programme read differently depending on where you looked. 90
 * and 60 is the stricter pair and the one a validation report needs.
 *
 * @param percent - Completion, 0 to 100.
 * @returns The tone it reads as.
 */
export function completionTone(percent: number): Tone {
  if (percent >= 90) {
    return "success";
  }
  if (percent >= 60) {
    return "warning";
  }
  return "error";
}

/** A validation flag's severity. */
export function severityTone(type: "error" | "warn" | "ok"): Tone {
  switch (type) {
    case "error":
      return "error";
    case "warn":
      return "warning";
    case "ok":
      return "success";
    default:
      return "neutral";
  }
}

/**
 * How far an invitation has got.
 *
 * `revoked` and `expired` are the two an administrator has to act on, and
 * `ui/StatusBadge` has always drawn both in error; this read them neutral.
 */
export function invitationTone(status: string): Tone {
  switch (status) {
    case "accepted":
      return "success";
    case "pending":
      return "warning";
    case "revoked":
    case "expired":
      return "error";
    default:
      return "neutral";
  }
}

/** Whether the invitation email reached the inbox. */
export function inviteEmailTone(status: string): Tone {
  switch (status) {
    case "sent":
      return "success";
    case "failed":
      return "error";
    case "pending":
      return "warning";
    default:
      return "neutral";
  }
}
